import { useCallback, useEffect, useState } from 'react'

import { PositionEditor } from '@/components/PositionEditor'
import { Card, Notice } from '@/components/ui'
import { api } from '@/lib/api'
import type { Instrument, Position } from '@/lib/types'

/**
 * Positions ouvertes, en vue logique.
 *
 * Choix explicite de l'utilisateur : **un seul résultat est affiché ici, celui
 * de la saisie**, pour suivre la stratégie. Le solde réel se consulte chez le
 * broker.
 *
 * Ce chiffre est donc l'OPPOSÉ du gain ou de la perte du compte : la position
 * réellement ouverte est l'inverse de la saisie. Chaque montant porte pour
 * cette raison la mention « stratégie », et un rappel figure sous la liste —
 * confondre les deux ferait prendre une perte pour un gain.
 */
export function PositionsPanel({
  brokerReady,
  currency,
  instruments,
}: {
  brokerReady: boolean
  currency: string
  instruments: Instrument[]
}) {
  const [positions, setPositions] = useState<Position[]>([])
  // `null` tant que la première réponse n'est pas arrivée. Sans cette
  // distinction, l'écran affiche « Aucune position ouverte » pendant le
  // chargement — un message alarmant et faux quand on en a une.
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [closing, setClosing] = useState<number | null>(null)
  const [confirming, setConfirming] = useState<number | null>(null)
  // Position en cours de modification : ouvre le graphique en plein écran.
  const [editing, setEditing] = useState<number | null>(null)

  const load = useCallback(async () => {
    try {
      setPositions(await api.positions())
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (!brokerReady) {
      setPositions([])
      setLoaded(false)
      return
    }
    void load()
    const timer = window.setInterval(load, 3000)
    return () => window.clearInterval(timer)
  }, [brokerReady, load])

  async function close(positionId: number) {
    setClosing(positionId)
    try {
      await api.closePosition(positionId)
      setConfirming(null)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fermeture impossible')
    } finally {
      setClosing(null)
    }
  }

  if (!brokerReady) {
    return (
      <Card title="Positions ouvertes">
        <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
          Broker non connecté : aucune position à afficher.
        </p>
      </Card>
    )
  }

  // Total de la vue logique, cohérent avec les lignes affichées.
  const total = positions.reduce(
    (sum, position) => sum + Number(position.entry_profit ?? 0),
    0,
  )

  /*
   * Devise des montants : celle que le SERVEUR attache à chaque position,
   * c'est-à-dire celle du compte réellement connecté.
   *
   * L'écran affichait jusqu'ici la devise saisie à la main dans le
   * calculateur — « EUR » par défaut. Sur un compte en dollars, chaque gain
   * et chaque perte portaient donc la mauvaise étiquette, sans que rien ne
   * l'indique. La valeur saisie ne sert plus que si aucune position n'est
   * ouverte, où elle n'étiquette aucun montant.
   */
  const liveCurrency = positions[0]?.account_currency ?? currency

  return (
    <div className="flex flex-col gap-3">
      {error && <Notice tone="error" title="Positions" items={[error]} />}

      <Card
        title={`Positions ouvertes (${loaded ? positions.length : '…'}) · stratégie`}
        action={
          positions.length > 0 && (
            <span
              className={`num text-[13px] font-semibold ${total >= 0 ? 'text-up' : 'text-down'}`}
            >
              {total >= 0 ? '+' : ''}
              {total.toFixed(2)} {liveCurrency}
            </span>
          )
        }
      >
        {!loaded ? (
          <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
            Lecture des positions chez le courtier…
          </p>
        ) : positions.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
            Aucune position ouverte.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {positions.map((position) => (
              <li key={position.position_id} className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="flex items-center gap-2">
                      <span
                        className={`num rounded px-1.5 py-0.5 text-[11px] font-bold ${
                          position.entry_side === 'BUY'
                            ? 'bg-up/20 text-up'
                            : 'bg-down/20 text-down'
                        }`}
                      >
                        {position.entry_side}
                      </span>
                      <span className="num text-[14px] text-ink">{position.symbol}</span>
                      <span className="num text-[12px] text-ink-muted">
                        {position.lots} lot
                      </span>
                    </p>
                    <p className="num mt-0.5 text-[11px] text-ink-muted">
                      entrée {position.entry_price}
                      {position.current_price && ` → ${position.current_price}`}
                    </p>
                  </div>

                  <div className="text-right">
                    {/* Résultat de la SAISIE. Ce n'est pas le solde du compte. */}
                    <p
                      className={`num text-[15px] font-semibold ${
                        Number(position.entry_profit ?? 0) >= 0 ? 'text-up' : 'text-down'
                      }`}
                    >
                      {position.entry_profit === null
                        ? '—'
                        : `${Number(position.entry_profit) >= 0 ? '+' : ''}${position.entry_profit} ${position.account_currency}`}
                    </p>
                    <p className="text-[9px] uppercase tracking-wide text-ink-muted">
                      stratégie
                    </p>
                  </div>
                </div>

                {/*
                  Chiffres au-dessus, actions en dessous sur téléphone.
                  Côte à côte, la ligne de données et les deux boutons
                  demandaient 450 px sur un écran qui en fait 375 : le texte se
                  tassait sous les boutons et « Fermer » venait à portée de
                  pouce d'un chiffre qu'on essayait de lire.
                */}
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  {/*
                    Vue stratégie, comme le reste de cet onglet : le niveau que
                    le courtier appelle « stop » est MA cible, et sa cible est
                    MON stop. Les afficher sous leurs noms de courtier au milieu
                    de chiffres de stratégie ferait lire le contraire de ce qui
                    se passe.
                  */}
                  <p className="num text-[11px] leading-relaxed text-ink-muted">
                    cible {position.stop_loss ?? '—'} · stop {position.take_profit ?? '—'} ·
                    swap {position.swap} · comm. {position.commission}
                  </p>

                  {confirming === position.position_id ? (
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => close(position.position_id)}
                        disabled={closing === position.position_id}
                        className="min-h-touch flex-1 rounded bg-down px-3 text-[13px] font-semibold text-white disabled:opacity-50 sm:flex-none"
                      >
                        {closing === position.position_id ? '…' : 'Confirmer'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(null)}
                        className="min-h-touch flex-1 rounded bg-elevated px-3 text-[13px] text-ink-muted sm:flex-none"
                      >
                        Annuler
                      </button>
                    </div>
                  ) : (
                    // Fermer une position est irréversible : on demande une
                    // confirmation plutôt que d'agir au premier effleurement.
                    <div className="flex shrink-0 gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(position.position_id)}
                        className="min-h-touch flex-1 rounded border border-border px-3 text-[13px] text-ink hover:bg-elevated/70 active:bg-elevated sm:flex-none sm:text-ink-muted"
                      >
                        Niveaux
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(position.position_id)}
                        className="min-h-touch flex-1 rounded border border-down/50 px-3 text-[13px] font-semibold text-down active:bg-down/10 sm:flex-none"
                      >
                        Fermer
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editing !== null &&
        (() => {
          const target = positions.find((p) => p.position_id === editing)
          if (!target) return null
          return (
            <PositionEditor
              position={target}
              instrument={instruments.find((i) => i.name === target.symbol)}
              onClose={() => setEditing(null)}
              onSaved={load}
            />
          )
        })()}

      {positions.length > 0 && (
        <p className="px-1 text-[11px] leading-relaxed text-ink-muted">
          Ces montants sont ceux de <strong className="text-ink">votre saisie</strong>, pas de
          votre compte. La position réellement ouverte étant l'inverse,{' '}
          <strong className="text-ink">le résultat du compte est exactement l'opposé</strong> :
          consultez votre broker pour le solde.
        </p>
      )}
    </div>
  )
}
